import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Calculate,
  TrendingUp,
  BarChart,
  ShowChart,
  PieChart,
} from '@mui/icons-material';
import APIKit from '../../../config/APIKit';
import { showSnackBar } from '../../store/compatibility';
import { useDispatch } from 'react-redux';
import style from '../settings/settings.module.scss';
import Title from '../settings/Title';
import ConfirmationDialog from '../../component/ConfirmationDialog';

const VISUALIZATION_ICONS = {
  number: <TrendingUp />,
  line_chart: <ShowChart />,
  bar_chart: <BarChart />,
  pie_chart: <PieChart />,
};

export default function KPIManagement() {
  const dispatch = useDispatch();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, kpi: null, mode: 'create' });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    id: null,
    loading: false,
  });

  useEffect(() => {
    loadKPIs();
  }, []);

  const loadKPIs = async () => {
    try {
      setLoading(true);
      const response = await APIKit.get('/kpis?is_active=true');
      if (response.success) {
        setKpis(response.data || []);
      }
    } catch (error) {
      console.error('Error loading KPIs:', error);
      dispatch(showSnackBar({ message: 'Error loading KPIs', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setModal({ open: true, kpi: null, mode: 'create' });
  };

  const handleEdit = (kpi) => {
    setModal({ open: true, kpi, mode: 'edit' });
  };

  const openConfirmDialog = (kpiId) => {
    setConfirmDialog({
      open: true,
      id: kpiId,
      loading: false,
    });
  };

  const closeConfirmDialog = () => {
    setConfirmDialog({
      open: false,
      id: null,
      loading: false,
    });
  };

  const handleDelete = async () => {
    if (!confirmDialog.id) return;
    
    setConfirmDialog(prev => ({
      ...prev,
      loading: true,
    }));

    try {
      await APIKit.delete(`/kpis/${confirmDialog.id}`);
      dispatch(showSnackBar({ message: 'KPI deleted successfully', severity: 'success' }));
      loadKPIs();
      closeConfirmDialog();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error deleting KPI', severity: 'error' }));
      setConfirmDialog(prev => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const handleCalculate = async (kpiId) => {
    try {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      await APIKit.post(`/kpis/${kpiId}/calculate`, {
        period_start: firstDay.toISOString().split('T')[0],
        period_end: lastDay.toISOString().split('T')[0],
        period_type: 'monthly',
      });

      dispatch(showSnackBar({ message: 'KPI calculated successfully', severity: 'success' }));
      loadKPIs();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error calculating KPI', severity: 'error' }));
    }
  };

  return (
    <div className={style.container}>
      <Title
        title="KPI Management"
        handleModal={handleCreate}
        enableButton={true}
      />

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {kpis.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                border: '1px solid rgba(0, 167, 142, 0.1)',
                position: 'relative',
                p: 4,
                textAlign: 'center',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: 'linear-gradient(90deg, #00a78e 0%, #0f766e 50%, #f093fb 100%)',
                  zIndex: 1,
                },
              }}
            >
              <Typography align="center" color="textSecondary" sx={{ py: 4 }}>
                No KPIs configured. Create your first KPI to get started.
              </Typography>
            </Box>
          </Grid>
        ) : (
          kpis.map(kpi => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={kpi.id}>
              <Card
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid rgba(0, 167, 142, 0.1)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.1)',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #00a78e 0%, #0f766e 50%, #f093fb 100%)',
                    zIndex: 1,
                  },
                }}
              >
                <CardContent sx={{ p: 3, pb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                      <Box
                        sx={{
                          background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                          p: 1.5,
                          borderRadius: '12px',
                          color: '#00a78e',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {VISUALIZATION_ICONS[kpi.visualization_type]}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                          }}
                        >
                          {kpi.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: '13px', mt: 0.5 }}>
                          {kpi.description || 'No description'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                    <Chip
                      label={kpi.category}
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                        color: '#00a78e',
                        fontWeight: 600,
                        borderWidth: '2px',
                      }}
                    />
                    <Chip
                      label={kpi.frequency}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(0, 167, 142, 0.3)',
                        color: '#00a78e',
                        fontWeight: 600,
                      }}
                    />
                    <Chip
                      label={kpi.visualization_type}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: 'rgba(0, 167, 142, 0.3)',
                        color: '#00a78e',
                        fontWeight: 600,
                      }}
                    />
                  </Box>

                  {kpi.target_value && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#00a78e',
                        mb: 1,
                      }}
                    >
                      Target: {parseFloat(kpi.target_value).toLocaleString()} {kpi.unit || ''}
                    </Typography>
                  )}

                  {kpi.last_calculated && (
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '11px' }}>
                      Last calculated: {new Date(kpi.last_calculated).toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
                <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <IconButton
                    size="small"
                    onClick={() => handleCalculate(kpi.id)}
                    title="Calculate"
                    sx={{
                      background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(56, 142, 60, 0.1) 100%)',
                      color: '#4caf50',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                        color: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                      },
                    }}
                  >
                    <Calculate fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(kpi)}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(0, 167, 142, 0.1) 0%, rgba(15, 118, 110, 0.1) 100%)',
                      color: '#00a78e',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
                        color: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(0, 167, 142, 0.3)',
                      },
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => openConfirmDialog(kpi.id)}
                    sx={{
                      background: 'linear-gradient(135deg, rgba(239, 83, 80, 0.1) 0%, rgba(229, 57, 53, 0.1) 100%)',
                      color: '#ef5350',
                      borderRadius: '8px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #ef5350 0%, #e53935 100%)',
                        color: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(239, 83, 80, 0.3)',
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      <ConfirmationDialog
        open={confirmDialog.open}
        onClose={closeConfirmDialog}
        onConfirm={handleDelete}
        title="Delete KPI?"
        message="Are you sure you want to delete this KPI? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
        confirmColor="error"
        loading={confirmDialog.loading}
      />

      <KPIModal
        open={modal.open}
        kpi={modal.kpi}
        mode={modal.mode}
        onClose={() => setModal({ open: false, kpi: null, mode: 'create' })}
        onSave={loadKPIs}
      />
    </div>
  );
}

function KPIModal({ open, kpi, mode, onClose, onSave }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    category: 'sales',
    data_source: 'database_query',
    query_definition: {
      entity_type: 'invoice',
      aggregation: 'sum',
      field: 'total',
      filters: {},
    },
    target_type: 'fixed',
    target_value: '',
    frequency: 'daily',
    visualization_type: 'number',
    unit: '',
    alert_enabled: false,
    alert_threshold: '',
    alert_condition: 'below',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (kpi) {
      setFormData({
        name: kpi.name || '',
        code: kpi.code || '',
        description: kpi.description || '',
        category: kpi.category || 'sales',
        data_source: kpi.data_source || 'database_query',
        query_definition: kpi.query_definition || {
          entity_type: 'invoice',
          aggregation: 'sum',
          field: 'total',
          filters: {},
        },
        target_type: kpi.target_type || 'fixed',
        target_value: kpi.target_value || '',
        frequency: kpi.frequency || 'daily',
        visualization_type: kpi.visualization_type || 'number',
        unit: kpi.unit || '',
        alert_enabled: kpi.alert_enabled || false,
        alert_threshold: kpi.alert_threshold || '',
        alert_condition: kpi.alert_condition || 'below',
      });
    }
  }, [kpi]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        target_value: formData.target_value ? parseFloat(formData.target_value) : null,
        alert_threshold: formData.alert_threshold ? parseFloat(formData.alert_threshold) : null,
      };

      if (mode === 'create') {
        await APIKit.post('/kpis', payload);
        dispatch(showSnackBar({ message: 'KPI created successfully', severity: 'success' }));
      } else {
        await APIKit.post(`/kpis/${kpi.id}`, payload);
        dispatch(showSnackBar({ message: 'KPI updated successfully', severity: 'success' }));
      }
      onSave();
      onClose();
    } catch (error) {
      dispatch(showSnackBar({ message: 'Error saving KPI', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
        }
      }}
    >
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '20px',
          py: 2.5,
          px: 3,
          mb: 0,
        }}
      >
        {mode === 'create' ? 'Create KPI' : 'Edit KPI'}
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 3, overflow: 'visible' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Grid container spacing={1.5}>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, pt: 2 }}>
                <TextField
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#00a78e',
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, pt: 2 }}>
                <TextField
                  label="Code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  fullWidth
                  required
                  placeholder="MONTHLY_REVENUE"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#00a78e',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5 }}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              minRows={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#00a78e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                    },
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#00a78e',
                },
              }}
            />
          </Box>

          <Grid container spacing={1.5}>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="category-label"
                    sx={{
                      '&.MuiInputLabel-root': {
                        backgroundColor: 'transparent',
                        px: 0.5,
                        transform: 'translate(14px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                  >
                    Category
                  </InputLabel>
                  <Select
                    labelId="category-label"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    label="Category"
                    sx={{
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="sales">Sales</MenuItem>
                    <MenuItem value="finance">Finance</MenuItem>
                    <MenuItem value="operations">Operations</MenuItem>
                    <MenuItem value="hr">HR</MenuItem>
                    <MenuItem value="marketing">Marketing</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="frequency-label"
                    sx={{
                      '&.MuiInputLabel-root': {
                        backgroundColor: 'transparent',
                        px: 0.5,
                        transform: 'translate(14px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                  >
                    Frequency
                  </InputLabel>
                  <Select
                    labelId="frequency-label"
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    label="Frequency"
                    sx={{
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Grid container spacing={1.5}>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="data-source-label"
                    sx={{
                      '&.MuiInputLabel-root': {
                        backgroundColor: 'transparent',
                        px: 0.5,
                        transform: 'translate(14px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                  >
                    Data Source
                  </InputLabel>
                  <Select
                    labelId="data-source-label"
                    value={formData.data_source}
                    onChange={(e) => setFormData({ ...formData, data_source: e.target.value })}
                    label="Data Source"
                    sx={{
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="database_query">Database Query</MenuItem>
                    <MenuItem value="calculated">Calculated</MenuItem>
                    <MenuItem value="manual">Manual</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
            <Grid size={{ md: 6 }}>
              <Box sx={{ mb: 1.5, mt: 0.5 }}>
                <FormControl fullWidth>
                  <InputLabel
                    id="visualization-type-label"
                    sx={{
                      '&.MuiInputLabel-root': {
                        backgroundColor: 'transparent',
                        px: 0.5,
                        transform: 'translate(14px, 16px) scale(1)',
                      },
                      '&.MuiInputLabel-shrink': {
                        transform: 'translate(14px, -9px) scale(0.75)',
                      },
                    }}
                  >
                    Visualization Type
                  </InputLabel>
                  <Select
                    labelId="visualization-type-label"
                    value={formData.visualization_type}
                    onChange={(e) => setFormData({ ...formData, visualization_type: e.target.value })}
                    label="Visualization Type"
                    sx={{
                      borderRadius: '12px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderRadius: '12px',
                      },
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    }}
                  >
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="line_chart">Line Chart</MenuItem>
                    <MenuItem value="bar_chart">Bar Chart</MenuItem>
                    <MenuItem value="pie_chart">Pie Chart</MenuItem>
                    <MenuItem value="percentage">Percentage</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          {formData.data_source === 'database_query' && (
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 4 }}>
                <Box sx={{ mb: 1.5, mt: 0.5 }}>
                  <FormControl fullWidth>
                    <InputLabel
                      id="entity-type-label"
                      sx={{
                        '&.MuiInputLabel-root': {
                          backgroundColor: 'transparent',
                          px: 0.5,
                          transform: 'translate(14px, 16px) scale(1)',
                        },
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                        },
                      }}
                    >
                      Entity Type
                    </InputLabel>
                    <Select
                      labelId="entity-type-label"
                      value={formData.query_definition.entity_type}
                      onChange={(e) => setFormData({
                        ...formData,
                        query_definition: { ...formData.query_definition, entity_type: e.target.value },
                      })}
                      label="Entity Type"
                      sx={{
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: '12px',
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00a78e',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00a78e',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                          },
                        },
                      }}
                    >
                      <MenuItem value="invoice">Invoice</MenuItem>
                      <MenuItem value="client">Client</MenuItem>
                      <MenuItem value="expense">Expense</MenuItem>
                      <MenuItem value="project">Project</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box sx={{ mb: 1.5, mt: 0.5 }}>
                  <FormControl fullWidth>
                    <InputLabel
                      id="aggregation-label"
                      sx={{
                        '&.MuiInputLabel-root': {
                          backgroundColor: 'transparent',
                          px: 0.5,
                          transform: 'translate(14px, 16px) scale(1)',
                        },
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -9px) scale(0.75)',
                        },
                      }}
                    >
                      Aggregation
                    </InputLabel>
                    <Select
                      labelId="aggregation-label"
                      value={formData.query_definition.aggregation}
                      onChange={(e) => setFormData({
                        ...formData,
                        query_definition: { ...formData.query_definition, aggregation: e.target.value },
                      })}
                      label="Aggregation"
                      sx={{
                        borderRadius: '12px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderRadius: '12px',
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00a78e',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00a78e',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                          },
                        },
                      }}
                    >
                      <MenuItem value="sum">Sum</MenuItem>
                      <MenuItem value="count">Count</MenuItem>
                      <MenuItem value="avg">Average</MenuItem>
                      <MenuItem value="min">Min</MenuItem>
                      <MenuItem value="max">Max</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <Grid size={{ xs: 4 }}>
                <Box sx={{ mb: 1.5 }}>
                  <TextField
                    label="Field"
                    value={formData.query_definition.field}
                    onChange={(e) => setFormData({
                      ...formData,
                      query_definition: { ...formData.query_definition, field: e.target.value },
                    })}
                    fullWidth
                    placeholder="total"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00a78e',
                            borderWidth: '2px',
                          },
                        },
                        '&.Mui-focused': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#00a78e',
                            borderWidth: '2px',
                            boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                          },
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#00a78e',
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  label="Target Value"
                  type="number"
                  value={formData.target_value}
                  onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#00a78e',
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Box sx={{ mb: 1.5 }}>
                <TextField
                  label="Unit"
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  fullWidth
                  placeholder="USD, %, etc."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                        },
                      },
                      '&.Mui-focused': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#00a78e',
                          borderWidth: '2px',
                          boxShadow: '0 0 0 4px rgba(0, 167, 142, 0.1)',
                        },
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#00a78e',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.alert_enabled}
                  onChange={(e) => setFormData({ ...formData, alert_enabled: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#00a78e',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#00a78e',
                    },
                  }}
                />
              }
              label="Enable Alerts"
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontWeight: 600,
                  color: '#00a78e',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            borderColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.4)',
              background: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.name || !formData.code}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            background: 'linear-gradient(135deg, #00a78e 0%, #0f766e 100%)',
            boxShadow: '0 4px 16px rgba(0, 167, 142, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0f766e 0%, #00a78e 100%)',
              boxShadow: '0 6px 20px rgba(0, 167, 142, 0.5)',
              transform: 'translateY(-2px)',
            },
            '&:disabled': {
              background: 'rgba(0, 167, 142, 0.5)',
            },
          }}
        >
          {loading
            ? mode === 'create'
              ? 'Creating...'
              : 'Updating...'
            : mode === 'create'
            ? 'Create'
            : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
